import React from 'react'
import Text from '../../atoms/Text'

const GRADE_COLOR_MAPPING = {
  A: {
    borderColor: 'border-[#039855]',
    arrowColor: '#039855',
    detailBgColor: 'bg-[#ECFDF3]',
    textColor: 'text-[#039855]',
    shadow: 'shadow-[4px_4px_0px_0px_#A6F4C5]',
    recommendationText: 'Highly Recommended',
    suggestionText:
      'This is an excellent choice for kidney health and overall well-being',
  },
  B: {
    borderColor: 'border-[#E7C144]',
    arrowColor: '#E7C144',
    detailBgColor: 'bg-[#FFFBEE]',
    textColor: 'text-[#826E2D]',
    shadow: 'shadow-[4px_4px_0px_0px_#FEF1C7]',
    recommendationText: 'Good in Moderation',
    suggestionText:
      'Suitable for consumption, but portion control is advised for balanced health',
  },
  C: {
    borderColor: 'border-[#DC6803]',
    arrowColor: '#DC6803',
    detailBgColor: 'bg-[#FEF0C7]',
    textColor: 'text-[#7A2E0E]',
    shadow: 'shadow-[4px_4px_0px_0px_#DC6803]',
    recommendationText: 'Consume with Care',
    suggestionText:
      'This item may pose risks if not limited — consider smaller portions',
  },
  D: {
    borderColor: 'border-[#D92D20]',
    arrowColor: '#D92D20',
    detailBgColor: 'bg-[#FEF3F2]',
    textColor: 'text-[#D92D20]',
    shadow: 'shadow-[4px_4px_0px_0px_#D92D20]',
    recommendationText: 'Avoid if Possible',
    suggestionText:
      'This item is not kidney-friendly and could negatively affect your health',
  },
}

const ItemCard = ({
  grade = 'A',
  title = '',
  showRecommendation = false,
  isClickable = false,
  onClick = () => {},
  foodDetails = null,
  keyNutrients = null,
}) => {
  return (
    <button
      disabled={!isClickable}
      onClick={onClick}
      className={`w-full rounded-4 border ${GRADE_COLOR_MAPPING[grade].borderColor} overflow-hidden ${GRADE_COLOR_MAPPING[grade].shadow}`}
    >
      <div className="p-4 flex flex-col gap-2 bg-white">
        {isClickable && (
          <div className="flex flex-row items-center justify-end">
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 0.0625C7.23233 0.0625 5.50436 0.586675 4.0346 1.56874C2.56483 2.55081 1.41929 3.94665 0.742831 5.57977C0.0663725 7.21288 -0.11062 9.00992 0.234236 10.7436C0.579091 12.4773 1.43031 14.0698 2.68024 15.3198C3.93017 16.5697 5.52268 17.4209 7.25638 17.7658C8.99009 18.1106 10.7871 17.9336 12.4202 17.2572C14.0534 16.5807 15.4492 15.4352 16.4313 13.9654C17.4133 12.4956 17.9375 10.7677 17.9375 9C17.935 6.6304 16.9926 4.35856 15.317 2.683C13.6414 1.00743 11.3696 0.0650023 9 0.0625ZM11.5489 9.48641L8.11141 12.9239C8.04753 12.9878 7.9717 13.0385 7.88825 13.073C7.80479 13.1076 7.71534 13.1254 7.625 13.1254C7.53467 13.1254 7.44522 13.1076 7.36176 13.073C7.27831 13.0385 7.20247 12.9878 7.1386 12.9239C7.07472 12.86 7.02405 12.7842 6.98948 12.7007C6.95491 12.6173 6.93712 12.5278 6.93712 12.4375C6.93712 12.3472 6.95491 12.2577 6.98948 12.1743C7.02405 12.0908 7.07472 12.015 7.1386 11.9511L10.0906 9L7.1386 6.04891C7.00959 5.9199 6.93712 5.74494 6.93712 5.5625C6.93712 5.38006 7.00959 5.2051 7.1386 5.07609C7.2676 4.94709 7.44257 4.87462 7.625 4.87462C7.80744 4.87462 7.98241 4.94709 8.11141 5.07609L11.5489 8.51359C11.6128 8.57744 11.6635 8.65327 11.6981 8.73673C11.7327 8.82019 11.7505 8.90965 11.7505 9C11.7505 9.09035 11.7327 9.17981 11.6981 9.26327C11.6635 9.34673 11.6128 9.42256 11.5489 9.48641Z"
                fill={GRADE_COLOR_MAPPING[grade].arrowColor}
              />
            </svg>
          </div>
        )}
        <Text
          tag={'p'}
          variant={'title'}
          size={'large'}
          className={'text-text-primary'}
        >
          {title.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())}
        </Text>
        {showRecommendation && (
          <div className="w-fit px-1.5 py-1 bg-GRAY-100 rounded-2">
            <Text
              tag={'p'}
              variant={'label'}
              size={'small'}
              bold={'true'}
              className={'text-text-primary'}
            >
              {GRADE_COLOR_MAPPING[grade].recommendationText}
            </Text>
          </div>
        )}
        <Text
          tag={'p'}
          variant={'body'}
          size={'medium'}
          className={'text-text-quartenary'}
        >
          {GRADE_COLOR_MAPPING[grade].suggestionText}
        </Text>
      </div>
      <div
        className={`${GRADE_COLOR_MAPPING[grade].detailBgColor} p-3 flex flex-col gap-2`}
      >
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-row items-center gap-2">
            <Text
              tag={'p'}
              variant={'label'}
              size={'medium'}
              className={`${GRADE_COLOR_MAPPING[grade].textColor}`}
            >
              Calories
            </Text>
            <Text
              tag={'p'}
              variant={'label'}
              size={'medium'}
              bold={'true'}
              className={`${GRADE_COLOR_MAPPING[grade].textColor}`}
            >
              {foodDetails?.calorie?.value ?? '--'}g
            </Text>
          </div>
          <div className="flex flex-row items-center gap-2">
            <Text
              tag={'p'}
              variant={'label'}
              size={'medium'}
              className={`${GRADE_COLOR_MAPPING[grade].textColor}`}
            >
              Protein
            </Text>
            <Text
              tag={'p'}
              variant={'label'}
              size={'medium'}
              bold={'true'}
              className={`${GRADE_COLOR_MAPPING[grade].textColor}`}
            >
              {foodDetails?.macronutrients?.protein?.value ?? '--'}g
            </Text>
          </div>
          <div className="flex flex-row items-center gap-2">
            <Text
              tag={'p'}
              variant={'label'}
              size={'medium'}
              className={`${GRADE_COLOR_MAPPING[grade].textColor}`}
            >
              Fat
            </Text>
            <Text
              tag={'p'}
              variant={'label'}
              size={'medium'}
              bold={'true'}
              className={`${GRADE_COLOR_MAPPING[grade].textColor}`}
            >
              {foodDetails?.macronutrients?.fats?.value ?? '--'}g
            </Text>
          </div>
        </div>
        {keyNutrients && (
          <div className="flex flex-row items-center justify-between">
            <div className="flex flex-row items-center gap-2">
              <Text
                tag={'p'}
                variant={'label'}
                size={'medium'}
                className={`${GRADE_COLOR_MAPPING[grade].textColor}`}
              >
                Sodium
              </Text>
              <Text
                tag={'p'}
                variant={'label'}
                size={'medium'}
                bold={'true'}
                className={`${GRADE_COLOR_MAPPING[grade].textColor}`}
              >
                {keyNutrients?.sodium?.value ?? '--'}
                {keyNutrients?.sodium?.unit ?? 'mg'}
              </Text>
            </div>
            <div className="flex flex-row items-center gap-2">
              <Text
                tag={'p'}
                variant={'label'}
                size={'medium'}
                className={`${GRADE_COLOR_MAPPING[grade].textColor}`}
              >
                Potassium
              </Text>
              <Text
                tag={'p'}
                variant={'label'}
                size={'medium'}
                bold={'true'}
                className={`${GRADE_COLOR_MAPPING[grade].textColor}`}
              >
                {keyNutrients?.potassium?.value ?? '--'}
                {keyNutrients?.potassium?.unit ?? 'mg'}
              </Text>
            </div>
            <div className="flex flex-row items-center gap-2">
              <Text
                tag={'p'}
                variant={'label'}
                size={'medium'}
                className={`${GRADE_COLOR_MAPPING[grade].textColor}`}
              >
                Water
              </Text>
              <Text
                tag={'p'}
                variant={'label'}
                size={'medium'}
                bold={'true'}
                className={`${GRADE_COLOR_MAPPING[grade].textColor}`}
              >
                {keyNutrients?.water_content?.value ?? '--'}
                {keyNutrients?.water_content?.unit ?? 'ml'}
              </Text>
            </div>
          </div>
        )}
      </div>
    </button>
  )
}

export default ItemCard
